import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest, HttpResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap, tap } from 'rxjs/operators';
import { SuccessDialogComponent } from '../shared/components/success-dialog/success-dialog.component';
import { ApiService } from '../services/api.service';
import { ConfigService } from '../services/config.service';
import { LoaderService } from '../services/loader.service';
import { SecondScreenService } from '../services/second-screen.service';
import { SessionCleanupService } from '../services/session-cleanup.service';
import { SessionRefreshService } from '../services/session-refresh.service';
import { StorageService } from '../services/storage.service';
import { ApiRequestHeadersService } from './api-request-headers.service';
import { AUTH_WEB_LOGOUT_PATH } from './auth-api-paths';
import { AuthHttpUrlPolicy } from './auth-http-url.policy';
import { shouldHideGlobalLoaderAfterHttp } from './interceptor-loader.routes';
import {
  Http401MetricContext,
  recordHttp401Metric,
} from './http-auth-telemetry';
import { buildRateLimitMessage } from './http-rate-limit-messages';
import { isLocalhostTokenServiceUrl } from '../utils/localhost-token-service.utils';

/**
 * Everything that reacts to HTTP outcomes: dialogs, refresh+retry, logout, loader.
 * Keeps the interceptor itself small and readable.
 */
@Injectable({ providedIn: 'root' })
export class SessionHttpErrorHandler {
  private labels: Record<string, string> | null = null;

  constructor(
    private loader: LoaderService,
    private router: Router,
    private dialog: MatDialog,
    private storageService: StorageService,
    private configService: ConfigService,
    private apiService: ApiService,
    private secondScreen: SecondScreenService,
    private sessionCleanup: SessionCleanupService,
    private sessionRefresh: SessionRefreshService,
    private headers: ApiRequestHeadersService,
    private urlPolicy: AuthHttpUrlPolicy,
  ) {
    this.storageService.getLabels().subscribe((labelsRes: Record<string, string> | null) => {
      if (labelsRes) {
        this.labels = { ...labelsRes };
      }
    });
  }

  /**
   * Full interceptor chain: 200 JSON token errors → refresh + one retry (before subscribers see the error body),
   * then normal response handling and 401/error path.
   */
  interceptChain(
    originalRequest: HttpRequest<unknown>,
    enrichedRequest: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return this.dispatchWithOptionalBodyRefresh(originalRequest, enrichedRequest, next, false).pipe(
      tap((event) => this.onResponse(event)),
      catchError((error: HttpErrorResponse) => this.handleError(originalRequest, enrichedRequest, next, error)),
    );
  }

  /** After a successful HTTP response: optional “token” errors in JSON body, then loader. */
  onResponse(event: HttpEvent<unknown>): void {
    if (!(event instanceof HttpResponse)) {
      return;
    }
    const body = event.body as Record<string, unknown> | null;
    const responseUrl = event.url || '';
    // Body-driven refresh already ran in dispatchWithOptionalBodyRefresh; if we still see this, session is dead.
    // Do not call onLogout again when logoutUser itself returned EMPTY/EXPIRED TOKEN.
    if (
      body &&
      'errorCode' in body &&
      this.bodyIndicatesSessionExpired(body) &&
      !responseUrl.includes(AUTH_WEB_LOGOUT_PATH)
    ) {
      const routeUrl = this.router.url.split('#')[0] || this.router.url;
      if (!this.urlPolicy.isAuthChromeUrl(routeUrl)) {
        const msg = this.labels?.['token_expiry_msg'] || 'Your session has expired. Please log in again.';
        this.apiService.onLogout({}).subscribe({
          next: () => this.finishSessionExpiredLogout(msg),
          error: () => this.finishSessionExpiredLogout(msg),
        });
      }
    }
    if (shouldHideGlobalLoaderAfterHttp(this.router.url)) {
      this.loader.hide();
    }
  }

  /** Maps HTTP errors to refresh, dialogs, or cleanup — same behavior as before the split. */
  handleError(
    originalRequest: HttpRequest<unknown>,
    enrichedRequest: HttpRequest<unknown>,
    next: HttpHandler,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    this.loader.hide();

    const status = Number(error.status);
    const requestUrl = enrichedRequest.url || error.url || '';
    const isBackendApi = this.isBackendApiRequest(requestUrl);
    const isLocalTokenRequest = isLocalhostTokenServiceUrl(requestUrl);
    const isCheckConfigRequest = /\/checkConfig(?:\?|$)/i.test(requestUrl);

    // Popups (400/403/429/404/502/504) only for backend BASE_URL calls — never for assets / localhost / other hosts.
    if (isBackendApi) {
      if (status === 400) {
        this.loader.clearBusyUi();
        this.showWarningFrom400(error);
        return throwError(() => error);
      }

      if (error.status === 403) {
        this.loader.clearBusyUi();
        this.showWarningFrom403(error);
        return throwError(() => error);
      }

      if (error.status === 429) {
        this.loader.clearBusyUi();
        this.showWarningFrom429(error, requestUrl);
        return throwError(() => error);
      }

      if (status === 404) {
        this.showWarningFrom404();
        return throwError(() => error);
      }

      if (status === 502 || status === 504) {
        this.showGatewayError(status);
        return throwError(() => error);
      }

      // Cross-origin gateway failures can be reduced by the browser to status 0
      // when the proxy's 502/504 response omits CORS headers.
      if (status === 0) {
        this.showGatewayError(502);
        return throwError(() => error);
      }
    }

    // Local token agent /checkConfig: complete silently (no popup), let callers fall back.
    if (isCheckConfigRequest && (status === 404 || status === 0)) {
      // this.loader.clearBusyUi();
      return throwError(() => error);
    }

    // Local USB/token agent errors must reach component catchError (clear isLoading).
    // Do not swallow as EMPTY the way we do for offline asset requests.
    if (status === 0 && isLocalTokenRequest) {
      // this.loader.clearBusyUi();
      return throwError(() => error);
    }

    // Cancelled/offline asset or non-API requests (e.g. translations-*.json).
    // Do not rethrow — prevents unhandled HttpErrorResponse noise in Sentry.
    if (status === 0 && !isBackendApi) {
      return EMPTY;
    }

    // Non-backend HTTP failures (assets, etc.): no popup; still propagate to subscribers.
    if (!isBackendApi && (status === 400 || status === 403 || status === 429 || status === 404 || status === 502 || status === 504)) {
      return throwError(() => error);
    }

    if (error.status === 401) {
      this.track401(error, enrichedRequest);
    }

    const routeUrl = this.router.url.split('#')[0] || this.router.url;

    if (this.urlPolicy.shouldAttemptRefresh(enrichedRequest, error, routeUrl)) {
      return this.sessionRefresh.refreshOnce().pipe(
        switchMap(() =>
          this.dispatchWithOptionalBodyRefresh(
            originalRequest,
            this.headers.enrich(originalRequest),
            next,
            false,
          ),
        ),
        tap((event) => this.onResponse(event)),
        catchError((refreshErr: HttpErrorResponse) => {
          if (refreshErr.status === 401) {
            recordHttp401Metric(refreshErr, enrichedRequest.url, 'logout');
          }
          if (!this.urlPolicy.isAuthChromeUrl(routeUrl)) {
            const msg = this.labels?.['token_expiry_msg'] || 'Your session has expired. Please log in again.';
            this.sessionCleanup.cleanUpAndRedirect({ sessionExpiredMessage: msg, screenId: '-5' });
          }
          return throwError(() => refreshErr);
        }),
      );
    }

    if (error.status === 401) {
      const failedUrl = error.url || enrichedRequest.url;
      if (!this.urlPolicy.isAuthChromeUrl(routeUrl) && !this.urlPolicy.isPublicNoRefreshUrl(failedUrl)) {
        const msg = this.labels?.['token_expiry_msg'] || 'Your session has expired. Please log in again.';
        this.sessionCleanup.cleanUpAndRedirect({ sessionExpiredMessage: msg, screenId: '-5' });
      }
    }

    return throwError(() => error);
  }

  /** True only for requests targeting the configured API BASE_URL (not assets / other hosts). */
  private isBackendApiRequest(url: string): boolean {
    const apiBaseUrl = this.configService.configIfReady?.BASE_URL?.replace(/\/$/, '');
    if (!apiBaseUrl || !url) {
      return false;
    }
    if (!(url === apiBaseUrl || url.startsWith(`${apiBaseUrl}/`))) {
      return false;
    }
    // Extra guard: never treat static asset paths as backend API, even if hosted under the same prefix.
    if (/\/assets\//i.test(url)) {
      return false;
    }
    return true;
  }

  /**
   * If the server returns 200 with EMPTY/INVALID/EXPIRED TOKEN (some gateways do this instead of 401),
   * run the same silent refresh as for 401 and retry the request once.
   */
  private dispatchWithOptionalBodyRefresh(
    originalRequest: HttpRequest<unknown>,
    enrichedRequest: HttpRequest<unknown>,
    next: HttpHandler,
    alreadyRetried: boolean,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(enrichedRequest).pipe(
      mergeMap((event: HttpEvent<unknown>) => {
        if (alreadyRetried || !(event instanceof HttpResponse)) {
          return of(event);
        }
        if (!this.bodyNeedsSilentRefresh(event.body, enrichedRequest.url)) {
          return of(event);
        }
        if (!this.urlPolicy.shouldAttemptSilentRefresh(originalRequest)) {
          return of(event);
        }
        const routeUrl = this.router.url.split('#')[0] || this.router.url;
        if (this.urlPolicy.isAuthChromeUrl(routeUrl)) {
          return of(event);
        }
        return this.sessionRefresh.refreshOnce().pipe(
          switchMap(() =>
            this.dispatchWithOptionalBodyRefresh(
              originalRequest,
              this.headers.enrich(originalRequest),
              next,
              true,
            ),
          ),
          catchError((refreshErr: HttpErrorResponse) => {
            if (!this.urlPolicy.isAuthChromeUrl(routeUrl)) {
              const msg =
                this.labels?.['token_expiry_msg'] || 'Your session has expired. Please log in again.';
              this.sessionCleanup.cleanUpAndRedirect({ sessionExpiredMessage: msg, screenId: '-5' });
            }
            return throwError(() => refreshErr);
          }),
        );
      }),
    );
  }

  /**
   * Normalize errorCode (string | string[]) for refresh-worthy session errors in a 200 body.
   * TODO(backend): drop INVALID REQUEST branch when the four orgmgmt lookups return EMPTY TOKEN.
   */
  private bodyNeedsSilentRefresh(body: unknown, requestUrl: string): boolean {
    if (this.bodyIndicatesSessionExpired(body)) {
      return true;
    }
    if (!this.urlPolicy.isInvalidRequestRefreshWorkaroundUrl(requestUrl)) {
      return false;
    }
    return this.bodyHasErrorCode(body, 'INVALID REQUEST');
  }

  private bodyHasErrorCode(body: unknown, code: string): boolean {
    if (!body || typeof body !== 'object' || !('errorCode' in body)) {
      return false;
    }
    const raw = (body as { errorCode?: string | string[] }).errorCode;
    const errorCodes = (Array.isArray(raw) ? raw : raw != null ? [raw] : []).map((c) => String(c));
    return errorCodes.includes(code);
  }

  private finishSessionExpiredLogout(message: string): void {
    this.dialog.closeAll();
    this.secondScreen.closeManualSignatureOnSessionEnd();
    this.sessionCleanup.cleanUpAndRedirect({
      sessionExpiredMessage: message,
      screenId: '-5',
    });
  }

  private track401(error: HttpErrorResponse, enrichedRequest: HttpRequest<unknown>): void {
    const routeUrl = this.router.url.split('#')[0] || this.router.url;
    const failedUrl = error.url || enrichedRequest.url;
    let context: Http401MetricContext = 'other';

    if (this.urlPolicy.shouldAttemptRefresh(enrichedRequest, error, routeUrl)) {
      context = 'refresh_eligible';
    } else if (this.urlPolicy.isPublicNoRefreshUrl(failedUrl)) {
      context = 'public_endpoint';
    } else if (this.urlPolicy.isAuthChromeUrl(routeUrl)) {
      context = 'auth_screen';
    } else {
      context = 'logout';
    }

    recordHttp401Metric(error, failedUrl, context);
  }

  private bodyIndicatesSessionExpired(body: unknown): boolean {
    return (
      this.bodyHasErrorCode(body, 'INVALID TOKEN') ||
      this.bodyHasErrorCode(body, 'EMPTY TOKEN') ||
      this.bodyHasErrorCode(body, 'EXPIRED TOKEN') ||
      this.bodyHasErrorCode(body, '600')
    );
  }

  private showWarningFrom400(error: HttpErrorResponse): void {
    const body = error.error;
    let fromBody = '';
    if (typeof body === 'object' && body) {
      if ('message' in body && (body as { message: unknown }).message) {
        fromBody = String((body as { message: unknown }).message);
      } else if ('errorDescription' in body) {
        const desc = (body as { errorDescription: unknown }).errorDescription;
        fromBody = Array.isArray(desc) ? String(desc[0] ?? '') : String(desc ?? '');
      }
    }
    const msg =
      (fromBody && fromBody.trim()) ||
      this.labels?.['bad_request_msg'] ||
      'Bad request. Please check your input and try again.';
    this.openWarning(msg, false);
  }

  private showWarningFrom403(error: HttpErrorResponse): void {
    const msg =
      (typeof error.error === 'object' &&
        error.error &&
        'message' in error.error &&
        String((error.error as { message: unknown }).message)) ||
      this.labels?.['access_forbidden_msg'] ||
      'You do not have permission for this action.';
    this.openWarning(msg, false);
  }

  /** Single app-wide 429 copy; subscribers must not show API body or a second dialog. */
  private showWarningFrom429(error: HttpErrorResponse, requestUrl: string): void {
    const lang = typeof localStorage !== 'undefined' ? localStorage.getItem('language') : null;
    const msg = buildRateLimitMessage(requestUrl, error, this.labels, lang);
    this.openWarning(msg, false);
  }

  /** Backend BASE_URL only: not-found / unavailable dialog. */
  private showWarningFrom404(): void {
    this.loader.clearBusyUi();
    this.openWarning(this.serviceUnavailableMessage(), false);
  }

  /** Backend BASE_URL only: bad gateway / gateway timeout dialog. */
  private showGatewayError(_status: number): void {
    this.loader.clearBusyUi();
    this.openWarning(this.serviceUnavailableMessage(), false);
  }

  private serviceUnavailableMessage(): string {
    return (
      this.labels?.['service_unavailable_msg'] ||
      'Service is currently unavailable. Please try again later.'
    );
  }

  private openWarning(message: string, isSuccess: boolean): void {
    const dialogRef = this.dialog.open(SuccessDialogComponent, {
      data: { message, isSuccess },
      // Keep above page-load overlays (z-index 999) so the message is visible.
      panelClass: 'gateway-error-dialog',
    });
    dialogRef.afterClosed().subscribe(() => this.dialog.closeAll());
  }
}
